/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, OnInit, inject, signal, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { MigrationApiService } from './migration-api.service';
import { MigrationService } from './migration.service';
import { MigrationJob } from './migration.models';

/**
 * Reconciliation — compares source and destination after a run. The numbers
 * only exist once the engine has compared both sides; until then the page
 * shows the check list it will perform and says so.
 */
@Component({
  selector: 'app-mig-reconciliation',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './migration-reconciliation.component.html'
})
export class MigrationReconciliationComponent implements OnInit {
  private readonly service = inject(MigrationService);
  readonly api = inject(MigrationApiService);

  readonly jobs = signal<MigrationJob[]>([]);
  readonly selectedId = signal('');
  readonly result = signal<any>(null);
  readonly busy = signal(false);

  readonly checks = [
    'DMS.MIGRATION.RECON_FOLDERS',
    'DMS.MIGRATION.RECON_DOCS',
    'DMS.MIGRATION.RECON_VERSIONS',
    'DMS.MIGRATION.RECON_SIZE',
    'DMS.MIGRATION.RECON_CHECKSUM',
    'DMS.MIGRATION.RECON_METADATA',
    'DMS.MIGRATION.RECON_PERMISSIONS'
  ];

  ngOnInit(): void {
    this.service.jobs().subscribe((list) => this.jobs.set(list));
  }

  pick(id: string): void {
    this.selectedId.set(id);
    this.result.set(null);
  }

  run(): void {
    const id = this.selectedId();
    if (!id || this.api.available() !== true) {
      return;
    }
    this.busy.set(true);
    this.api.reconcile(id).subscribe({
      next: (res) => {
        this.busy.set(false);
        this.result.set(res);
      },
      error: () => {
        this.busy.set(false);
        this.result.set({ error: true });
      }
    });
  }

  trackByRow(i: number): number {
    return i;
  }
}
